/**
 * PreviewMode — shows the story exactly as a reader would see it: cover,
 * title, description, author, chapter list, and content.
 */

import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Send } from 'lucide-react';
import { fadeUp } from '@/lib/motion';
import { Button, cx } from '@/components/site/primitives';
import StoryCover from './StoryCover';
import { getStoryWordCount, getReadingTime } from './utils/storyHelpers';

export default function PreviewMode({ story, onClose, onPublish }) {
  const [activeChapterId, setActiveChapterId] = useState(story.chapters[0]?.id);
  const activeChapter = story.chapters.find((c) => c.id === activeChapterId) ?? story.chapters[0];
  const wordCount = getStoryWordCount(story);

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="fixed inset-0 z-50 overflow-y-auto bg-cream"
      data-testid="my-writing-preview"
    >
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-cream/90 px-5 py-3 backdrop-blur">
        <span className="text-xs font-semibold uppercase tracking-wide text-subtle">Preview · as readers will see it</span>
        <div className="flex items-center gap-2">
          {story.status !== 'published' && onPublish && (
            <Button variant="primary" icon={false} onClick={onPublish} className="px-4 py-2 text-xs">
              <Send className="mr-1.5 h-3.5 w-3.5" /> Publish
            </Button>
          )}
          <button type="button" onClick={onClose} aria-label="Back to editor" data-testid="my-writing-preview-close"
            className="grid h-9 w-9 place-items-center rounded-full text-subtle hover:bg-card hover:text-ink">
            <X className="h-4.5 w-4.5" />
          </button>
        </div>
      </div>

      <div className="u-container grid gap-10 py-10 md:grid-cols-[220px_1fr] md:py-14">
        <div>
          <StoryCover story={story} className="h-56 w-40 shadow-lift" />
          <h1 className="mt-5 font-display text-2xl font-semibold leading-tight text-ink">{story.title}</h1>
          <p className="mt-1 text-sm text-subtle">by You</p>
          {story.description && <p className="mt-3 text-sm leading-relaxed text-subtle">{story.description}</p>}
          <p className="mt-3 text-xs text-subtle">{story.genre} · {wordCount.toLocaleString()} words · {getReadingTime(wordCount)} min read</p>

          <div className="mt-6 space-y-1">
            {story.chapters.map((ch, i) => (
              <button
                key={ch.id}
                type="button"
                onClick={() => setActiveChapterId(ch.id)}
                className={cx(
                  'block w-full rounded-lg px-2.5 py-1.5 text-left text-sm transition-colors',
                  ch.id === activeChapterId ? 'bg-coral/10 font-semibold text-coral' : 'text-subtle hover:bg-card hover:text-ink'
                )}
              >
                {i + 1}. {ch.title}
              </button>
            ))}
          </div>
        </div>

        <div className="mx-auto w-full max-w-[680px]">
          {activeChapter ? (
            <>
              <h2 className="font-display text-3xl font-semibold text-ink">{activeChapter.title}</h2>
              <div
                className="prose-writing mt-6 font-display text-[19px] leading-[1.85] text-ink [&_blockquote]:border-l-2 [&_blockquote]:border-coral/50 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-subtle [&_h2]:mt-6 [&_h2]:text-2xl [&_h2]:font-semibold [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-4 [&_ul]:list-disc [&_a]:text-coral [&_a]:underline"
                dangerouslySetInnerHTML={{ __html: activeChapter.content || '<p class="text-subtle">This chapter is empty.</p>' }}
              />
            </>
          ) : (
            <p className="text-sm text-subtle">This story has no chapters yet.</p>
          )}
        </div>
      </div>
    </motion.div>
  );
}
